import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { apiFetch } from './api/client';
import i18n from '../i18n';

const TOKEN_KEY = 'goodvale_native_push_token';
let initialization;
let registration;
const device = token => ({ platform: Capacitor.getPlatform(), token, language: i18n.language?.startsWith('en') ? 'en' : 'de' });

export async function initNativePushNotifications(onNotificationAction) {
  if (!Capacitor.isNativePlatform()) return;
  if (!initialization) initialization = (async () => {
    await PushNotifications.addListener('pushNotificationReceived', notification => {
      if (localStorage.getItem('goodvale_token')) void showLocalNotification(notification.title || 'Goodvale', notification.body || '', notification.data);
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', action => onNotificationAction?.(action.notification?.data));
    await LocalNotifications.addListener('localNotificationActionPerformed', action => onNotificationAction?.(action.notification?.extra));
  })().catch(error => { initialization = null; throw error; });
  await initialization;
  if (localStorage.getItem(TOKEN_KEY) && localStorage.getItem("goodvale_token")) {
    const permission = await PushNotifications.checkPermissions();
    if (permission.receive === "granted") await registerNativePush();
  }
}

export async function getNativePushStatus() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!Capacitor.isNativePlatform() || !token) return false;
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive !== 'granted') return false;
  const result = await apiFetch('/notifications/native-push/status', { method: 'POST', body: JSON.stringify(device(token)) });
  return result.enabled;
}

export async function registerNativePush() {
  if (!Capacitor.isNativePlatform()) return { supported: false };
  if (registration) return registration;
  registration = (async () => {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return { supported: true, granted: false };
    const session = localStorage.getItem('goodvale_token');
    let successListener, errorListener, timer;
    try {
      const token = await new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error(i18n.t('notifications.push.failed'))), 15000);
        (async () => {
          successListener = await PushNotifications.addListener('registration', result => resolve(result.value));
          errorListener = await PushNotifications.addListener('registrationError', () => reject(new Error(i18n.t('notifications.push.failed'))));
          await PushNotifications.register();
        })().catch(reject);
      });
      if (!session || session !== localStorage.getItem('goodvale_token')) throw new Error(i18n.t('notifications.push.failed'));
      await apiFetch('/notifications/native-push', { method: 'POST', body: JSON.stringify(device(token)) });
      if (session !== localStorage.getItem('goodvale_token')) {
        await apiFetch('/notifications/native-push', { method: 'DELETE', headers: { Authorization: 'Bearer ' + session }, body: JSON.stringify(device(token)) });
        throw new Error(i18n.t('notifications.push.failed'));
      }
      localStorage.setItem(TOKEN_KEY, token);
      const local = await LocalNotifications.checkPermissions();
      if (local.display !== 'granted') await LocalNotifications.requestPermissions();
      return { supported: true, granted: true };
    } finally {
      clearTimeout(timer);
      await successListener?.remove();
      await errorListener?.remove();
    }
  })();
  try { return await registration; } finally { registration = null; }
}

export async function unregisterNativePush({ forceLocal = false } = {}) {
  if (!Capacitor.isNativePlatform()) return;
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    if (token) await apiFetch('/notifications/native-push', { method: 'DELETE', body: JSON.stringify(device(token)), keepalive: true });
  } finally {
    if (forceLocal) {
      try { await PushNotifications.unregister(); } finally { localStorage.removeItem(TOKEN_KEY); }
    }
  }
  if (!forceLocal) { await PushNotifications.unregister(); localStorage.removeItem(TOKEN_KEY); }
}

export async function showLocalNotification(title, body, data = {}) {
  if (!Capacitor.isNativePlatform()) return;
  const permission = await LocalNotifications.checkPermissions();
  if (permission.display !== 'granted') return;
  await LocalNotifications.schedule({ notifications: [{ id: Date.now() % 2147483647, title, body, extra: data }] });
}
